import type { ReactNode } from "react";
import { useLoyaltiesOverviewQuery } from "Api/Queries/Loyalty/useLoyaltiesOverviewQuery";
import { ExplanationHint } from "Components/Loyalty/Shared/Overview/ExplanationHint";
import { MyShareValue } from "Components/Loyalty/Shared/Overview/MyShareValue";
import { Overview } from "Components/Loyalty/Shared/Overview/Overview";
import { PayoutValue } from "Components/Loyalty/Shared/Overview/PayoutValue";
import { PointsValue } from "Components/Loyalty/Shared/Overview/PointsValue";
import { RewardsValue } from "Components/Loyalty/Shared/Overview/RewardsValue";
import {
  type Resources,
  getResourcePath,
  useResource,
} from "Translations/Resources";
import { formatCurrency } from "Utils/CurrencyUtils";

type ValueResources = typeof Resources.Pages.Content.Stardust.Overview.Points;

export type LoyaltyOverviewConfig = {
  pointsResources: ValueResources;
  pointsIcon: ReactNode;
  payoutResources: ValueResources;
  rewardsResources: ValueResources;
  myShareResources: ValueResources;
  RulesLink: ReactNode;
};

type Props = {
  config: LoyaltyOverviewConfig;
};

export const LoyaltyOverviewSection = ({ config }: Props) => {
  const { t } = useResource();
  const { data, isError, isLoading } = useLoyaltiesOverviewQuery();
  const {
    pointsResources,
    pointsIcon,
    payoutResources,
    rewardsResources,
    myShareResources,
    RulesLink,
  } = config;

  const pointsValue = data?.userPoints?.points
    ? new Intl.NumberFormat("cs-CZ").format(data.userPoints.points)
    : "0";

  const payoutValue = data?.toPayout
    ? formatCurrency({
        amount: data.toPayout.value,
        currencyCode: data.toPayout.currencyCode,
      })
    : "0";

  const rewardsValue = data?.poolTotal
    ? formatCurrency({
        amount: data.poolTotal.value,
        currencyCode: data.poolTotal.currencyCode,
      })
    : "0";

  const myShareValue = data?.userShare
    ? formatCurrency({
        amount: data.userShare.value,
        currencyCode: data.userShare.currencyCode,
      })
    : "0";

  return (
    <Overview
      isError={isError}
      pointsTile={
        <PointsValue
          isLoading={isLoading}
          value={pointsValue}
          title={t(pointsResources.title)}
          icon={pointsIcon}
          explanationHint={
            <ExplanationHint
              i18nKey={getResourcePath(pointsResources.explanationHint)}
              trailing={RulesLink}
            />
          }
        />
      }
      payoutTile={
        <PayoutValue
          isLoading={isLoading}
          value={payoutValue}
          title={t(payoutResources.title)}
          explanationHint={
            <ExplanationHint
              i18nKey={getResourcePath(payoutResources.explanationHint)}
            />
          }
        />
      }
      rewardsTile={
        <RewardsValue
          isLoading={isLoading}
          value={rewardsValue}
          title={t(rewardsResources.title)}
          explanationHint={
            <ExplanationHint
              i18nKey={getResourcePath(rewardsResources.explanationHint)}
            />
          }
        />
      }
      myShareTile={
        <MyShareValue
          isLoading={isLoading}
          value={myShareValue}
          title={t(myShareResources.title)}
          explanationHint={
            <ExplanationHint
              i18nKey={getResourcePath(myShareResources.explanationHint)}
            />
          }
        />
      }
      rulesLink={RulesLink}
    />
  );
};
